import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Form, Input, message, Modal, Select } from 'antd';
import { BasketService } from '../services';
import { useParams } from 'react-router-dom';
import { BasketContext } from '../contexts';
import { useTranslation } from 'react-i18next';
import { CountryContext } from '@modules/country';
import { SettingsContext } from '@modules/settings';
import { useBackgroundNavigate, useSearchParams } from '@shared/hooks';
import { localURLMaker } from '@shared/utils'

export const CreateBasket = () => {
  const { t } = useTranslation();
  const navigate = useBackgroundNavigate();
  const { id } = useParams<{ id: string }>();
  const { searchParams } = useSearchParams<{ name?: string }>();
  const [country] = useContext(CountryContext);

  const [name, setName] = useState('');
  const [countryId, setCountryId] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { reFetchBaskets } = useContext(BasketContext);

  const { getCountryId } = useContext(SettingsContext);

  const handleChangeName = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleChangeCountry = useCallback((value: string | undefined) => {
    setCountryId(value);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    if (!countryId) {
      message.error(t('countries.choose'));
      setIsSubmitting(false)
      return;
    }

    const result = await BasketService.createOrUpdate({ id, name, countryId });

    if (result.status === 200) {
      await reFetchBaskets();
      navigate(localURLMaker('/baskets', {}, { reFetchBasketTable: true }));
    } else {
      message.error(result.data);
    }
    setIsSubmitting(false);
  }, [countryId, navigate, id, name, reFetchBaskets, t]);

  useEffect(() => {
    const countryId = getCountryId(country.selected);

    if (countryId) {
      setCountryId(countryId.toString());
    }
  }, [country.selected, getCountryId]);

  useEffect(() => {
    if (searchParams.name) {
      setName(searchParams.name);
    }
  }, [searchParams.name]);

  return (
    <Modal
      open={true}
      width={576}
      onCancel={() => navigate('/baskets')}
      title={t('common.actions.createWithName', { name: t('baskets.basket').toLocaleLowerCase() })}
      onOk={handleSubmit}
      confirmLoading={isSubmitting}
    >
      <Form size='large' layout='vertical'>
        <Form.Item label={t('common.fields.country')}>
          <Select value={countryId} onChange={handleChangeCountry} placeholder={t('common.placeholders.country')} allowClear={false}>
            <Select.Option disabled={!country.available.includes('tr')} value={getCountryId('tr')?.toString() || ''}>
              {t('countries.tr')}
            </Select.Option>
            <Select.Option disabled={!country.available.includes('us')} value={getCountryId('us')?.toString() || ''}>
              {t('countries.us')}
            </Select.Option>
            <Select.Option disabled={!country.available.includes('es')} value={getCountryId('es')?.toString() || ''}>
              {t('countries.es')}
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label={t('common.fields.name')}>
          <Input placeholder={t('baskets.placeholders.name')} onChange={handleChangeName} value={name} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
